import React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import MenuIcon from '@mui/icons-material/Menu'
import Button from '@mui/material/Button'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { useAppDispatch, useAppSelector } from 'store'
import styled from 'styled-components'

import logoWhite from 'assets/logoWhite.png'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { useHandleSearchParams } from 'hooks/useHandleSearchParams'
import {
  getUserEmail,
  getUserFullName,
  getUserRole,
  logout,
  selectUserPermissions} from 'services/authSlice'
import { KEYS, localStorageService } from 'utils/localStorage'
import { PATHS } from 'utils/paths'
import { USER_ROLES } from 'utils/usersRole'

import { DEFAULT_PAGINATION } from './Menu'

interface NavBarProps {
  menuOpen: boolean
  handleOpenMenu: () => void
  handleCloseMenu: () => void
}

export const NavBar = ({
  menuOpen,
  handleOpenMenu,
  handleCloseMenu
}: NavBarProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const userFullName = useAppSelector(getUserFullName)
  const userEmail = useAppSelector(getUserEmail)
  const userRole = useAppSelector(getUserRole)
  const userPermissions = useAppSelector(selectUserPermissions)
  const { country } = useHandleSearchParams()
  const isAdminPanelEnabled = useFeatureFlag('adminPanel')
  const isAiTranslationsEnabled = useFeatureFlag('aiTranslations')
  const isLandingPagesEnabled = useFeatureFlag('landingPages')
  const storedMenuMode = localStorageService.get(KEYS.menuMode)
  const homePath =
    isLandingPagesEnabled && storedMenuMode === 'landing-pages'
      ? `${PATHS.landingPages}?country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}&brand=All`
      : `${PATHS.products}?country=${country}&size=${DEFAULT_PAGINATION.size}&page=${DEFAULT_PAGINATION.page}&brand=All`
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    dispatch(logout())
  }

  return (
    <Wrapper>
      <Left>
        <MenuIcon onClick={menuOpen ? handleCloseMenu : handleOpenMenu} />
      </Left>
      <Center>
        <HomeLink to={homePath}>
          <Logo src={logoWhite} alt='logo' />
        </HomeLink>
      </Center>
      <Right>
        <Button
          id='basic-button'
          size='medium'
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup='true'
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
        >
          <AccountCircleOutlinedIcon />
          {userFullName}
        </Button>
        <Menu
          id='basic-menu'
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button'
          }}
        >
          <MenuItem disabled>
            {USER_ROLES[userRole.name as keyof typeof USER_ROLES]}
          </MenuItem>
          <MenuItem disabled>{userEmail}</MenuItem>
          {userPermissions?.can_view_admin_panel && isAdminPanelEnabled && (
            <MenuItem
              onClick={() => {
                navigate(PATHS.admin)
                handleClose()
              }}
            >
              Admin Panel
            </MenuItem>
          )}
          {(userPermissions?.can_view_context ||
            userPermissions?.can_view_context_list) &&
            isAdminPanelEnabled &&
            isAiTranslationsEnabled && (
              <MenuItem
                onClick={() => {
                  navigate(PATHS.aiContext)
                  handleClose()
                }}
              >
                AI Context
              </MenuItem>
            )}
          <MenuItem
            onClick={() =>
              window.open(
                `${PATHS.privacyPolicy}`,
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            Privacy Policy
          </MenuItem>
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </Right>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: ${({ theme }) => theme.dimensions.navBarHeight + 'px'};
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => theme.zIndex.height};
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  padding: 8px 16px;
  box-shadow: ${({ theme }) => theme.shadows.navBar};
`
const Left = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    margin-right: 20px;
    cursor: pointer;
  }
`
const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 18px;

  svg {
    margin-right: 10px;
  }

  .MuiButton-root {
    color: ${({ theme }) => theme.colors.white};
    link-transform: unset;
    text-transform: none;
  }
`

const Center = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
`
const HomeLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  border: 0;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-decoration: none;
  color: inherit;
`
const Logo = styled.img`
  height: ${({ theme }) => theme.dimensions.logoWhiteHeight + 'px'};
`
